import React, { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import ConfigurationCard from './components/configurationCard';
import { useTranslation } from 'react-i18next';
import UserInformation from './components/userInformation';
import AddressInformation from './components/addressInformation';
import { useGetCities, useGetCountries, useGetStates, useUser } from '@/hooks';
import { getDocumentTypeIdAnalizer } from '@/helpers/concierge';
import IdentificationCard from './components/identificationCard';
import { WelletContext } from '@/context/wellet/welletContext';
import { getAllRoles, getAllSalesTeamLead, getQrLinkget, postUser, putUser } from '@/services';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { removeTimezoneOffset } from '@/helpers/dates';
import JobInformation from './components/jobInformation';
import CommissionCard from './components/comissionCard';
import { Show } from '@/components';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import AdvantageBanner from './components/advantageBanner';
import { Button, Toast } from '@/uiComponents/common';
import { useLocation, useNavigate } from 'react-router-dom';
import Banner from '@/components/banners/banner';
import { CheckFile } from '@/uiComponents/icons';
import { AlertTriangle } from 'lucide-react';

const UsersForm = ({
  id = null,
  mode,
  userQuery,
  profilePic,
  profilePicBase64,
  formRef,
  setIsSaving,
  showDocumentationBanner,
  canSeeDocumentation,
  handleClickBannerDoc,
  showKycPending,
  affiliateType,
}) => {
  const organization = useSelector((state) => state.app.selectedOrganization);
  const language = useSelector((state) => state.app.language);
  const qrCodeSelector = useSelector((state) => state.app.qrCode);
  const welletContext = useContext(WelletContext);
  const { t } = useTranslation('translation', {
    keyPrefix: 'screens.profile',
  });
  const { t: tGeneric } = useTranslation('translation', { keyPrefix: 'generic' });
  const { t: translate } = useTranslation('translation', { keyPrefix: 'forms.userForm' });
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useUser();
  const [showToast, setShowToast] = useState(false);

  const documentTypes = getDocumentTypeIdAnalizer(t);
  const gender = [
    { label: translate('selects.gender.male'), value: 'M' },
    { label: translate('selects.gender.female'), value: 'F' },
  ];

  const resetData = (data) => {
    const body = {
      sid: data?.sid,
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.email,
      phone: data?.phone,
      status: data?.isActive ? 'ACTIVE' : 'INACTIVE',
      sector: data?.sector,
      jobTitle: data?.jobTitle,
      company: data?.company,
      companyId: data?.organizationId,
      dateOfBirth: removeTimezoneOffset(data?.dateOfBirth, true),
      nationality: data?.nationality,
      documentType: documentTypes.find((item) => item.value === data?.documentType)?.value,
      documentNumber: data?.documentNumber,
      address: data?.address,
      country: data?.country?.id ? `${data?.country?.id}` : null,
      state: data?.city ? `${data?.city?.state?.id}` : null,
      city: data?.city ? `${data?.city?.id}` : null,
      isActive: data?.isActive,
      advantageNetworkStatus: data?.advantageNetworkStatus,
      language: data?.language,
      gender: gender?.find((g) => g.value === data?.gender)?.value
        ? gender?.find((g) => g.value === data?.gender)?.value
        : null, // select
      internRef: data?.internRef,
      link: data?.link,
      withdrawalMethod: data?.withdrawalMethod ? `${data?.withdrawalMethod}` : null,
      roles: data?.roles?.map((role) => role) ?? [],
      reportsToUserId: data?.reportToUserId ? `${data.reportToUserId}` : `null`,
      commissionPlusPercentage: data?.commissionPlusPercentage,
      isCommissionDisabled: data?.isCommissionDisabled,
      teamCommissionId: data?.teamCommissionId ? `${data?.teamCommissionId}` : null,
      profilePic: profilePicBase64 ? profilePicBase64 : null,
      hasFamiliar: data?.hasFamilyWorking ? 'false' : 'true',
      hasWorked: data?.hasWorkedPreviously ? 'false' : 'true',
      corporateEmail: data?.corporateEmail,
      webCompany: data?.webCompany,
      socialNetworkCompany: data?.socialNetworkCompany,
      officeAddressCompany: data?.officeAddressCompany,
      notes: data?.notes,
      workCredentialImage: data?.workCredentialImage ? data?.workCredentialImage : null,
      affiliateType: data?.affiliateType,
      workCredentialStatus: data?.workCredentialStatus ? data?.workCredentialStatus : null,
    };
    return body;
  };

  const methods = useForm({
    defaultValues: resetData(userQuery?.data?.data),
  });

  const queryClient = useQueryClient();

  const bossQuery = useQuery({
    queryKey: ['sales-team-leader', organization.marketId],
    queryFn: () => getAllSalesTeamLead(welletContext, organization.marketId),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !roles.some((r) => r === 'affiliatecenter-onlyview') && mode !== 'PROFILE',
  });

  const rolesQuery = useQuery({
    queryKey: ['roles-dragaret'],
    queryFn: () => getAllRoles(welletContext),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !roles.some((r) => r === 'affiliatecenter-onlyview') && mode !== 'PROFILE',
  });

  const countriesQuery = useGetCountries(language);
  const statesQuery = useGetStates(methods?.watch('country'), {
    enabled: !!methods?.watch('country'),
  });
  const citiesQuery = useGetCities(
    methods?.watch('state'),
    {
      enabled: !!methods?.watch('state'),
    },
    true,
  );

  const qrData = userQuery?.data?.data?.link;
  const qrUrl = `api/qr?format=1&logo=${qrCodeSelector}&url=${qrData}`;

  const userQrLinkGet = useQuery({
    queryKey: ['boxQrGet', qrUrl],
    queryFn: () => getQrLinkget(welletContext, qrUrl),
    ...defaultQueryOptions,
    enabled: mode === 'CONCIERGE' && !!qrData,
  });

  const countries = countriesQuery?.data?.data?.map((v) => ({ label: v.name, value: `${v.id}` }));
  const states = statesQuery?.data?.data?.map((v) => ({ label: v.name, value: `${v.id}` }));
  const cities = citiesQuery?.data?.data?.map((v) => ({ label: v.name, value: `${v.id}` }));

  const boss = bossQuery?.data?.data || [];
  const bossOptions = [
    { label: t('withoutRp'), value: `null` },
    ...boss.map((r) => ({
      label: `${r.firstName} ${r.lastName}`,
      value: `${r.id}`,
    })),
  ];

  const rolesFormat = rolesQuery?.data?.data.map((r) => ({
    label: r.prettyName,
    value: `${r.name}`,
  }));

  const refetchStatus = () => {
    userQuery.refetch().then(() => profilePic.refetch());
    queryClient.refetchQueries({ queryKey: ['count-sidebar', organization.marketId] });
  };

  const onSubmit = (data) => {
    const body = {
      id: id === false ? null : data?.sid,
      firstName: data?.firstName,
      lastName: data?.lastName,
      phone: data?.phone,
      email: data?.email,
      sector: data?.sector,
      // profilePic: data?.profilePic?.base64 ? data?.profilePic?.base64 : null,
      profilePic: profilePicBase64 ? profilePicBase64 : null,
      jobTitle: data?.jobTitle,
      company: data?.company,
      nationality: data?.nationality,
      isActive: !id ? true : data?.status === 'ACTIVE' ? true : false,
      documentNumber: data?.documentNumber,
      dateOfBirth: data?.dateOfBirth,
      documentType: data?.documentType,
      countryId: data?.country, // To Do: armar input
      cityId: data?.city ?? null, // To Do: armar input
      address: data?.address,
      language: data?.language ?? 'es',
      advantageNetworkStatus: data?.advantageNetworkStatus,
      gender: data.gender ? data.gender : null,
      internRef: data.internRef,
      WithdrawalMethodType: data?.withdrawalMethod,
      roles: data.roles?.map((r) => r),
      reportsToUserId: data.reportsToUserId !== 'null' ? data.reportsToUserId : null,
      commissionPlusPercentage: Number(data.commissionPlusPercentage) ?? null,
      isCommissionDisabled: data.isCommissionDisabled ? data.isCommissionDisabled : false,
      companyId: data.companyId ? data.companyId : organization.id,
      corporateEmail: data?.corporateEmail,
      webCompany: data?.webCompany,
      socialNetworkCompany: data?.socialNetworkCompany,
      officeAddressCompany: data?.officeAddressCompany,
      notes: data?.notes,
      affiliateType: data?.affiliateType,
    };
    // setFullName(`${data?.firstName} ${data?.lastName}`);
    if (!id) {
      body['password'] = data.password;
    }
    userMutation.mutate(body);
  };

  const showInfo = userQuery?.data?.data?.roles?.includes('associate', 'salesteamlead');
  const imageUrl = userQrLinkGet.isSuccess
    ? URL.createObjectURL(new Blob([userQrLinkGet?.data?.data], { type: 'image/jpeg' }))
    : null;

  const userMutation = useMutation({
    mutationFn: (body) => {
      if (id === false) return postUser(welletContext, body);
      return putUser(welletContext, body);
    },
    onMutate: () => {
      setIsSaving(true);
    },
    onSuccess: () => {
      setShowToast(true);
      setIsSaving(false);
    },
    onError: (error) => {
      setShowToast(true);
      setIsSaving(false);
    },
  });

  const rolText =
    affiliateType === 'INDEPENDENT'
      ? tGeneric('affiliate')
      : affiliateType === 'CONCIERGE'
      ? tGeneric('concierge')
      : tGeneric('internal_staff');

  const toastSuccess =
    mode === 'CONCIERGE'
      ? t('toast.success.concierge', {
          name: rolText,
        })
      : mode === 'PROFILE'
      ? t('toast.success.profile')
      : !id
      ? t('toast.success.usersCreate')
      : t('toast.success.users');

  return (
    <>
      <FormProvider {...methods}>
        <form key={'user'} onSubmit={methods.handleSubmit(onSubmit)}>
          <button ref={formRef} type='submit' style={{ display: 'none' }} />
          <AdvantageBanner
            id={id}
            mode={mode}
            organization={organization}
            roles={roles}
            refetchStatus={refetchStatus}
            t={t}
          />
          <Show.When isTrue={showKycPending && mode === 'CONCIERGE'}>
            <Banner
              title={tGeneric('alerts.kycStatusNone.title')}
              text={tGeneric('alerts.kycStatusNone.text', { name: rolText })}
              icon={<AlertTriangle className='tw-h-5 tw-w-5' />}
              endComponent={null}
            />
          </Show.When>
          <Show.When
            isTrue={canSeeDocumentation && showDocumentationBanner && mode === 'CONCIERGE'}
          >
            <Banner
              title={translate('bannerDoc.title')}
              text={translate('bannerDoc.description')}
              icon={<CheckFile size={20} />}
              endComponent={
                <Button
                  text={translate('bannerDoc.button')}
                  className={' btn-general btn-wellet-primary'}
                  size={'medium'}
                  onClick={handleClickBannerDoc}
                  type='button'
                />
              }
            />
          </Show.When>
          <div className='tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-6'>
            <div className='tw-flex tw-flex-col tw-col-span-3  md:tw-col-span-2 '>
              <UserInformation
                t={t}
                className={'tw-mb-6'}
                id={id}
                mode={mode}
                phone={userQuery?.data?.data?.phone}
              />
              <AddressInformation
                t={t}
                className={'tw-mb-6'}
                countries={countries}
                states={states}
                cities={cities}
              />
              <IdentificationCard
                t={t}
                className={'tw-mb-6'}
                documentTypes={documentTypes}
                gender={gender}
              />
              {mode === 'CONCIERGE' ? null : (
                <JobInformation
                  t={t}
                  className={'tw-mb-6'}
                  mode={mode}
                  id={id}
                  refetchStatus={refetchStatus}
                  affiliateType={affiliateType}
                />
              )}
            </div>

            <div className='tw-flex tw-flex-col tw-col-span-3  md:tw-col-span-1 '>
              <ConfigurationCard
                t={t}
                className={'tw-mb-6'}
                mode={mode}
                bossOptions={bossOptions}
                rolesFormat={rolesFormat}
                id={id}
                rolText={rolText}
                refetchStatus={refetchStatus}
              />
              <Show.When isTrue={mode === 'CONCIERGE' || (mode === 'USER' && showInfo)}>
                <CommissionCard
                  t={t}
                  teamCommissionId={userQuery?.data?.data?.teamCommissionId}
                  id={id}
                  showInfo={showInfo}
                  mode={mode}
                  imageUrl={imageUrl}
                  imgLoading={userQrLinkGet.isLoading}
                  rolText={rolText}
                />
              </Show.When>
            </div>
          </div>
        </form>
      </FormProvider>
      <Show.When isTrue={userMutation.isSuccess && showToast}>
        <Toast
          message={toastSuccess}
          type={`SUCCESS`}
          onCallback={() => {
            if (!id) navigate('/settings/team-and-security');
            else {
              navigate(location.pathname, {
                state: {
                  ...location.state,
                  sellerName: `${userMutation.data?.data?.firstName} ${userMutation.data?.data?.lastName}`,
                },
                replace: true,
              });
            }
            setIsSaving(false);
            userQuery.refetch();
          }}
        />
      </Show.When>
      <Show.When isTrue={userMutation.isError && showToast}>
        <Toast
          message={
            userMutation?.error?.response?.data?.errorCode === 'REPORTS_TO_SELF'
              ? t('toast.errorReportsToSelf')
              : mode === 'CONCIERGE'
              ? t('toast.errors', { name: tGeneric('promoter') })
              : t('toast.errorUser')
          }
          type={`ERROR`}
          onCallback={() => null}
        />
      </Show.When>
    </>
  );
};

export default UsersForm;
